import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/dateFmt';
import { ClipboardCheck, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { RejectTaskModal } from '../../components/onboarding/RejectTaskModal';
import {
  useApproveTask,
  useRejectTask,
  useTaskReviews,
  type TaskReviewRow,
} from '../../hooks/useTaskReviews';
import type { RejectEmailRecipient } from '@hcc/types';
import { ApiError } from '../../lib/api';

/**
 * Global "Tasks to review" queue: every awaiting_review task across all
 * people, oldest submission first. HR opens this in the morning and walks
 * the queue. Each row links to the dedicated review screen so HR can see
 * the actual submission before deciding; Approve / Request changes are also
 * available inline for the quick cases.
 */
export function TaskReviews() {
  const { data: rows, isLoading, error } = useTaskReviews();
  const approve = useApproveTask();
  const reject = useRejectTask();
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<TaskReviewRow | null>(null);

  async function onApprove(row: TaskReviewRow) {
    setBusyTaskId(row.task.task_id);
    try {
      await approve.mutateAsync({ personId: row.person.id, taskId: row.task.task_id });
      toast.success(`Approved: ${row.task.title}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'approve failed');
    } finally {
      setBusyTaskId(null);
    }
  }

  async function onReject(reason: string, recipient: RejectEmailRecipient) {
    if (!rejecting) return;
    const row = rejecting;
    setBusyTaskId(row.task.task_id);
    try {
      await reject.mutateAsync({
        personId: row.person.id,
        taskId: row.task.task_id,
        reason,
        recipient,
      });
      const to =
        recipient === 'work'
          ? row.person.email
          : row.person.personal_email ?? row.person.email;
      toast.success(`Sent. The reason will be emailed to ${to}.`);
      setRejecting(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'reject failed');
    } finally {
      setBusyTaskId(null);
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-warning-soft/15 text-warning-soft-fg">
            <ClipboardCheck className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-warning-soft-fg">
              HR queue
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Tasks to review
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Every task submitted by an inductee that's awaiting your approval.
              Oldest first. Open one to see what they sent.
            </p>
          </div>
        </header>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error instanceof ApiError ? error.message : 'load failed'}</span>
          </div>
        ) : null}

        <div className="mt-6 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {isLoading || !rows ? (
            <SkeletonList rows={5} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ClipboardCheck className="h-6 w-6" />}
              title="Nothing to review"
              description="When an inductee submits a task that needs HR approval, it'll appear here."
            />
          ) : (
            <ul className="divide-y divide-divider/10">
              {rows.map((row) => (
                <li key={row.task.task_id} className="px-4 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <Link
                      to={`/admin/task-reviews/${row.person.id}/${row.task.task_id}`}
                      className="group min-w-0 flex-1"
                    >
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted">
                        {row.person.full_name}
                        <ChevronRight className="h-3 w-3" />
                        <span>{row.person.position ?? 'Position unknown'}</span>
                      </span>
                      <div className="mt-1 text-sm font-semibold group-hover:text-accent">
                        {row.task.title}
                      </div>
                      {row.task.description ? (
                        <p className="mt-0.5 whitespace-pre-wrap text-xs text-fg-muted">
                          {row.task.description}
                        </p>
                      ) : null}
                      <div className="mt-1 text-[11px] tabular-nums text-fg-muted">
                        Submitted{' '}
                        {row.task.submitted_at ? formatDateTime(row.task.submitted_at) : '-'}
                      </div>
                      <span className="mt-2 inline-block text-xs font-medium text-accent group-hover:underline">
                        Review submission →
                      </span>
                    </Link>
                    <div className="flex shrink-0 flex-col gap-1.5">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onApprove(row)}
                        loading={busyTaskId === row.task.task_id && approve.isPending}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setRejecting(row)}
                        disabled={busyTaskId === row.task.task_id}
                      >
                        Request changes
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <RejectTaskModal
        open={!!rejecting}
        taskTitle={rejecting?.task.title ?? ''}
        workEmail={rejecting?.person.email ?? ''}
        personalEmail={rejecting?.person.personal_email ?? null}
        busy={reject.isPending}
        onCancel={() => setRejecting(null)}
        onSubmit={onReject}
      />
    </main>
  );
}
